import { Pencil } from 'lucide-react';
import { Button } from '../Button';
import { ConfidenceBadge } from './ConfidenceBadge';
import { useT } from '../../i18n';
import { CONFIDENCE_REQUIRED_STAGES, type Inquiry } from '../../types/api';
import styles from './ConfidenceControl.module.css';

export interface ConfidenceControlProps {
  inquiry: Inquiry;
  /** Opens the confidence dialog (rendered by TransitionMenu). */
  onEdit: () => void;
}

/**
 * Customer feedback — confidence is a deal property, not a funnel
 * action, so its entry point lives next to the confidence badge in the
 * drawer meta row (the dialog itself stays in TransitionMenu).
 *
 * Badge set → pencil icon button; unset → compact «Задать».
 * Issue #42 rule kept: before «Опция возможностей» the control is
 * disabled with the reason shown next to it.
 */
export function ConfidenceControl({ inquiry, onEdit }: ConfidenceControlProps) {
  const { t } = useT();
  const lateStage = CONFIDENCE_REQUIRED_STAGES.includes(inquiry.stage);

  if (inquiry.outcome !== 'NONE') {
    return <ConfidenceBadge confidence={inquiry.confidence} />;
  }

  return (
    <span className={styles.control}>
      {inquiry.confidence ? (
        <>
          <ConfidenceBadge confidence={inquiry.confidence} />
          <span title={!lateStage ? t('transitions.confidenceAvailableFrom') : undefined}>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={t('transitions.changeConfidence')}
              leftIcon={<Pencil size={13} />}
              onClick={onEdit}
              disabled={!lateStage}
            />
          </span>
        </>
      ) : (
        <span title={!lateStage ? t('transitions.confidenceAvailableFrom') : undefined}>
          <Button variant="ghost" size="sm" onClick={onEdit} disabled={!lateStage}>
            {t('transitions.setConfidenceShort')}
          </Button>
        </span>
      )}
      {!lateStage && <span className={styles.hint}>{t('transitions.confidenceAvailableFrom')}</span>}
    </span>
  );
}
